/**
 * ThemedButton — Layer-1 button matching the Scholarly Modernist design system.
 *
 * Variants:
 *   primary     — filled dark ink (#1A1917), white text, warm shadow
 *   secondary   — white bg, light border, hover fills with warm surface
 *   ghost       — gold-text color, no border, 0.75 opacity on hover
 *   destructive — error-bordered, red text, error-bg on hover
 *
 * Sizes: sm (32px) | md (40px) | lg (48px) | full (48px pill, auth screens)
 * Icon-only: icon-sm | icon-md | icon-lg
 *
 * All colors driven by CSS utilities in globals.css (@layer utilities).
 * No inline style hacks — hover states handled by CSS pseudo-classes.
 *
 * @layer-1 — propagates to all features automatically
 */
import { cn } from "@/shared/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef } from "react";

const themedButtonCVA = cva(
  [
    "inline-flex items-center justify-center gap-1.5",
    "rounded-[var(--radius-lg)] font-semibold",
    "transition-all duration-200 cursor-pointer",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
    "disabled:opacity-50 disabled:pointer-events-none",
    "select-none",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "btn-primary-colors border-none hover:translate-y-[-1px] focus-visible:ring-[var(--btn-primary-bg)]",
        secondary:
          "btn-secondary-colors border hover:translate-y-[-1px] focus-visible:ring-[var(--btn-secondary-border)]",
        ghost:
          "btn-ghost-colors border-none bg-transparent focus-visible:ring-[var(--gold-text)]",
        destructive:
          "btn-destructive-colors border bg-transparent focus-visible:ring-[var(--color-error)]",
      },
      size: {
        sm:   "h-8 px-3 text-xs",
        md:   "h-10 px-[18px] text-[13px]",
        lg:   "h-12 px-6 text-sm",
        full: "w-full h-12 px-6 text-sm font-bold rounded-[var(--radius-xl)]",
        "icon-sm":  "size-8",
        "icon-md":  "size-10",
        "icon-lg":  "size-12",
      },
    },
    defaultVariants: {
      variant: "primary",
      size:    "md",
    },
  }
);

/* ── Types ─────────────────────────────────────────────────────────────── */

type ButtonBaseProps = VariantProps<typeof themedButtonCVA> & {
  /** Icon rendered before the label (inline-start). */
  iconStart?: React.ReactNode;
  /** Icon rendered after the label (inline-end). */
  iconEnd?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
};

export type ThemedButtonProps = ButtonBaseProps &
  Omit<ComponentPropsWithoutRef<"button">, keyof ButtonBaseProps>;

export type ThemedAnchorButtonProps = ButtonBaseProps &
  Omit<ComponentPropsWithoutRef<"a">, keyof ButtonBaseProps> & {
    as: "a";
  };

/* ── Component ─────────────────────────────────────────────────────────── */

export function ThemedButton({
  variant,
  size,
  className,
  iconStart,
  iconEnd,
  children,
  ...props
}: ThemedButtonProps) {
  const isIconOnly =
    (size?.toString().startsWith("icon-") ?? false) && !children;

  return (
    <button
      className={cn(themedButtonCVA({ variant, size }), className)}
      {...props}
    >
      {!isIconOnly && iconStart}
      {children}
      {!isIconOnly && iconEnd}
    </button>
  );
}

/**
 * ThemedAnchorButton — same design as ThemedButton but rendered as an <a>.
 * Use for navigation actions where a link is semantically correct.
 */
export function ThemedAnchorButton({
  variant,
  size,
  className,
  iconStart,
  iconEnd,
  children,
  as: _as,
  ...props
}: ThemedAnchorButtonProps) {
  const isIconOnly =
    (size?.toString().startsWith("icon-") ?? false) && !children;

  return (
    <a
      className={cn(themedButtonCVA({ variant, size }), className)}
      {...props}
    >
      {!isIconOnly && iconStart}
      {children}
      {!isIconOnly && iconEnd}
    </a>
  );
}
